define('services/notificationService',[
    'services/loggingService',
    'lib/knockout',
    'lib/lodash4',
    'moment',
    'jquery',
    'lib/bluebird',
    'config',
    'services/authService',
    'services/apiCore',
    'util/ExponentialIntervalGenerator',
    'util/PubSub'
], function (
    LOG,
    ko,
    _,
    moment,
    $,
    Promise,
    config,
    authService,
    apiCore,
    ExponentialIntervalGenerator,
    pubSub
) {
    'use strict';

    var NOTIFICATION_URI = '/notifications/channels';
    var HB_TIMER_EXPIRATION = 35000;
    var hbTimer;

    var options;
    var connection;
    var defaults = {
        useWebSocket: true,
        maxReconnectInterval: 5*1000, //5 seconds
        nsRoot: config,
        reconnectTimeout: 30*60*1000 //30 minutes
    };

    options = $.extend({}, defaults, config.notifications || {});

    var exponentialBackoff = new ExponentialIntervalGenerator(options.maxReconnectInterval);
    var reconnectTimeoutObj = null;
    var randomId = ko.observable();
    var lastConnectionTime = moment.now();
    var connectUri;

    /**
     * Connect to a notification stream via websockets.
     * @param {object} params parameters required for connection
     * @return {WebSocket} The newly created websocket object
     */
    var connect = function () {
        if (connectUri) {
            if (connection) {
                connection.onclose = _.noop;
                connection.close();
                clearTimeout(hbTimer);
                notificationService.connected(false);
            }
            connection = new WebSocket(connectUri);

            connection.onopen = function () {
                resetHeartbeatExpirationTimer();

                LOG.debug('WS: open');
                notificationService.connected(true);

                // we've created a connection, so lets reset the interval if we lose it
                // again in the future
                exponentialBackoff.reset();

                if (reconnectTimeoutObj) {
                    // remove the timeout callback if we connect before the timeout has been
                    // executed
                    clearTimeout(reconnectTimeoutObj);
                }

                lastConnectionTime = moment.now();
            };

            connection.onclose = function () {
                LOG.debug('WS: close');
                clearTimeout(hbTimer);

                //flag as disconnected
                notificationService.connected(false);

                //re-connect if we're still logged in
                if (authService.principalId()) {
                    if (moment.duration(moment().diff(lastConnectionTime)) < options.reconnectTimeout) {
                        var reconnectInterval = exponentialBackoff.getInterval();
                        exponentialBackoff.fail();
                        LOG.debug('WS: reconnect attempt in ', reconnectInterval, arguments);
                        reconnectTimeoutObj = setTimeout(connect, reconnectInterval);
                    } else {
                        if (reconnectTimeoutObj) {
                            clearTimeout(reconnectTimeoutObj);
                            reconnectTimeoutObj = null;
                        }
                        LOG.debug('WS: unable to reconnect within ' + options.reconnectTimeout + 'ms. Pausing reconnection attempts.');
                    }
                }
            };

            connection.onerror = function (event) {
                LOG.warn('WS: error', event); //browser will also trigger close
            };

            connection.onmessage = function (messageEvent) {
                var data = messageEvent.data;
                if (data) {
                    try {
                        var json = JSON.parse(data);
                        if (json.topicName === "channel.metadata") {
                            LOG.debug('WS Status:', json);
                            resetHeartbeatExpirationTimer();
                        }
                        else {
                            var eventBody;
                            var options = {};

                            try {
                                eventBody = json.eventBody;

                                if (_.has(json, "metadata")) {
                                    options.metadata = json.metadata;
                                }

                                LOG.info('WS Topic: %s,  Body: %O', json.topicName, eventBody);
                            }
                            catch (err) {
                                LOG.error('WS Error: Unable to parse event body.', json.eventBody, err);
                                LOG.error(err.message, err.stack);
                                throw err;
                            }

                            pubSub.Topic(json.topicName).publish(eventBody, options);
                        }
                    }
                    catch (err) {
                        LOG.error('WS Error: Unable to parse envelope.', data, err);
                        throw err;
                    }
                }
            };
        }
    };

    var disconnect = function () {
        LOG.debug('WS: Attempt to disconnect connection');
        if (connection) {
            connection.close();
        }
        pubSub.clearAllTopics();
        if (subscriptionPromise) {
            subscriptionPromise.cancel();
        }
        if(activeSubscriptions) {
            activeSubscriptions.removeAll();
        }
    };

    var resetHeartbeatExpirationTimer = function() {
        if (hbTimer) {
            clearTimeout(hbTimer);
        }

        hbTimer = setTimeout(function() {
            LOG.warn('WS: heartbeat timer expired for user', ko.unwrap(authService.principalId));
            if (connection) {
                connection.close();
            }
        }, HB_TIMER_EXPIRATION);
    };

    // Track all active subscription requests. When consumers subscribe or unsubscribe,
    // notificationService will update this observableArray, triggering a PUT of all active
    // subscription topics, ensuring the requested collection is consistent with all changes
    var activeSubscriptions = ko.observableArray();

    // Promise tracks pending requests to PUT all active subscription requests
    var subscriptionPromise = Promise.resolve();

    var consumerPromiseResolvers = [];

    function getConnectUri() {
        return apiCore.v2.post(NOTIFICATION_URI)
            .then(function (response) {
                randomId(response.id);
                connectUri = response.connectUri;
            });
    }

    function setupConnection() {
        var userId = authService.principalId();
        if (userId) {
            // post to get URI
            getConnectUri()
                .then(connect)
                .catch(function (e) {
                    LOG.error('Unable to connect to carrier pigeon', e);
                });
        } else {
            disconnect();
        }
    }

    function start() {
        // Check how long since our last connection. If it's been awhile,
        // reset with a new channel
        $(window).on('focus.checkConnectionTime', function () {
            var now = moment.now();
            if (moment.duration(moment(now).diff(lastConnectionTime)) > options.reconnectTimeout) {
                lastConnectionTime = now;
                setupConnection();
            }
        });

        //Connect if we're already logged in &
        //Trigger the asynchronous connect/disconnect actions on login/logout
        ko.computed(setupConnection);

        // Computed to PUT all the active subscription requests when the collection changes.
        // Rate Limited to 200ms after changes to the collection have stopped, to debounce
        // in case a consumer (or multiple consumers) make simultaneous (or multiple subsequent)
        // changes to subscriptions
        ko.computed(function () {
            if(subscriptionPromise) {
                subscriptionPromise.cancel();
            }
            if (activeSubscriptions().length > 0 && randomId()) {
                // unique ids from active subscriptions
                var subscriptions = _(activeSubscriptions()).map(function (item) {
                        return { id: item.id };
                    })
                    .uniqBy('id')
                    .value();
                subscriptionPromise = apiCore.v2.put(NOTIFICATION_URI + '/' + randomId() + '/subscriptions', subscriptions)
                    .then(function () {
                        //resolve all pending consumer promises and clear them
                        _.forEach(consumerPromiseResolvers, function (resolver) {
                            resolver.resolve();
                        });
                        consumerPromiseResolvers = [];
                        LOG.info('Subscribed to multiple topics.', activeSubscriptions().slice());
                    })
                    .catch(Promise.CancellationError, _.noop)
                    .catch(function (error) {
                        //reject all pending consumer promises and clear them
                        _.forEach(consumerPromiseResolvers, function (resolver) {
                            resolver.reject();
                        });
                        consumerPromiseResolvers = [];
                        LOG.error('Failed to subscribe to multiple topics.', activeSubscriptions().slice(), error);
                        throw error;
                    })
                    .finally(function () {
                        subscriptionPromise = null;
                    });
            } else if (activeSubscriptions().length === 0) {
                _.forEach(consumerPromiseResolvers, function (resolver) {
                    resolver.resolve();
                });
                consumerPromiseResolvers = [];
            }
        }).extend({ rateLimit: { timeout: 200, method: "notifyWhenChangesStop" } });
    }

    //helper function to defer a consumer promise
    var defer = function() {
        var resolve,reject;
        var promise = new Promise(function() {
            resolve = arguments[0];
            reject = arguments [1];
        });
        consumerPromiseResolvers.push({
            resolve: resolve,
            reject: reject
        });
        return promise;
    };

    var notificationService = {
        start: _.once(start),

        /**
         * An observable attribute that indicates whether the client is connected to the
         * notification service backend.
         *
         * @type {Observable Boolean} true if connected, false otherwise.
         */
        connected: ko.observable(false),

        /**
         * Subscribe a callback to events that are published to a topic key.
         *
         * @param  {String}   topic - A string indicating the key name of a topic that
         *  will receive notification events.
         * @param  {Function} callback - A functions that will execute when a notification
         *  event is published to the provided topic.
         * @return {Promise} A promise that is resolved when the subscription is
         *  successful
         */
        subscribe: function (topicId, callback) {
            var newSubscription = { id: topicId, callback: callback };
            var existingSubscription = _.find(activeSubscriptions.peek(), newSubscription);
            if (!existingSubscription) {
                activeSubscriptions.push(newSubscription);
            }

            return defer()
                    .then(function () {
                        pubSub.Topic(topicId).subscribe(callback);
                    });
        },

        /**
         * Subscibe a to collection of topics with a callback for each one.
         *
         * @param  {Map} batchObj A map that contains the set of topics the should be
         *  subscribed to and the callback that should execute for each one.
         *
         *  The key of a map entry will be used as the name of the topic to subscribe to.
         *
         *  The value of a map entry is expected to be a function that will be executed
         *  whenever an event is published to the topic.
         *
         *  Example:
         *
         *      { "t1": function () {} }
         *
         *  In the example, "t1" will be used as the topic to subscribe to and the value
         *  function will be used as the callback.
         *
         * @return {Promise} A promise object that is resolved once all topics have
         *  successfully subscribed with the service.
         */
        batchSubscribe: function (batchObj) {
            var newSubscriptions = [];

            _.forIn(batchObj, function (callback, topicId) {
                var newSubscription = { id: topicId, callback: callback };
                var existingSubscription = _.find(activeSubscriptions.peek(), newSubscription);
                if (!existingSubscription) {
                    newSubscriptions.push(newSubscription);
                }
            });
            ko.utils.arrayPushAll(activeSubscriptions, newSubscriptions);

            return defer()
                    .then(function () {
                        _.forEach(newSubscriptions, function(topic) {
                            var callback = batchObj[topic.id];
                            pubSub.Topic(topic.id).subscribe(callback);
                        });
                    });
        },

        /**
         * Unsubscribe a callback from events that are published to a topic key.
         *
         * @param  {String}   topic - A string indicating the key name of the topic to
         *  remove the subcribed callback from.
         * @param  {Function} callback - A function that has subscribed to the provided
         *  topic that will stop being executed when notification events are sent to the
         *  topic.
         * @return {Promise} A promise that is resolved when the unsubscription is
         *  successful
         */
        unsubscribe: function (topicId, callback) {
            LOG.info('Unsubscribing from "' + topicId + '".');

            pubSub.Topic(topicId).unsubscribe(callback);

            var existingSubscription = _.find(activeSubscriptions.peek(), { id: topicId, callback: callback });
            if (existingSubscription) {
                activeSubscriptions.remove(existingSubscription);
            }

            return defer();
        },

        /**
         * Unsubscribe a collection of callbacks from a set of topics.
         *
         * @param  {Map} batchObj A map that contains the set of topics the should be
         *  unsubscribed to and the callback that should be removed for each one.
         *
         *  The key of a map entry will be used as the name of the topic to unsubscribe
         *  from.
         *
         *  The value of a map entry is expected to be a function that will be removed
         *  from the topic.
         *
         *  Example:
         *
         *      { "t1": function () {} }
         *
         *  In the example, "t1" will be used as the topic key to unsubscribe from and the
         *  function should be an instance of the callback function to remove from the
         *  list of callbacks for a topic.
         *
         * @return {Promise} A promise object that is resolved once all topics have
         *  successfully subscribed with the service.
         */
        batchUnsubscribe: function (batchObj) {
            var subscriptions = activeSubscriptions.peek();

            _.forIn(batchObj, function (callback, topicId) {
                pubSub.Topic(topicId).unsubscribe(callback);

                var existingSubscription = _.find(subscriptions, { id: topicId, callback: callback });
                if (existingSubscription) {
                    _.remove(subscriptions, existingSubscription);
                }
            });

            activeSubscriptions(subscriptions);

            return defer();
        }
    };

    notificationService.start();

    return notificationService;
});

